import { REAL_ESTATE_PLAN_MARKETING_PRODUCT_CODES } from '@crm/shared/real-estate-plan-presets';
import {
  commercialProducts,
  type ProductCode,
} from '../config/commercialProducts';
import {
  getPlanStartingPriceUsd,
  type BillingCadence,
  type PlanCode,
} from '../config/commercialPlans';
import {
  SERVER_RENDERED_ROOT_ATTRIBUTE,
  isServerRenderedMarketingRoute,
} from '../config/serverRendering';
import {
  CRM_MARKETING_SITE_URL,
  MARKETPLACE_SITE_URL,
  buildCrmMarketingUrl,
  indexedMarketingRoutes,
  legacyProductMarketingRoutes,
  segmentMarketingRoutes,
  type IndexedMarketingRoute,
  type SegmentMarketingRoute,
} from '../config/marketingRoutes';

type JsonPrimitive = string | number | boolean | null;
type MarketingTranslationValue =
  JsonPrimitive | MarketingTranslationObject | MarketingTranslationValue[];

export interface MarketingTranslationObject {
  readonly [key: string]: MarketingTranslationValue;
}

export interface MarketingPrerenderLocale {
  readonly ai: MarketingTranslationObject;
  readonly common: MarketingTranslationObject;
  readonly comparison: MarketingTranslationObject;
  readonly home: MarketingTranslationObject;
  readonly modules: MarketingTranslationObject;
  readonly pricing: MarketingTranslationObject;
  readonly segments: MarketingTranslationObject;
}

export interface SegmentFaq {
  readonly question: string;
  readonly answer: string;
}

export interface MarketingPrerenderPage {
  readonly route: IndexedMarketingRoute;
  readonly title: string;
  readonly description: string;
  readonly canonicalUrl: string;
  readonly structuredDataId: string;
  readonly structuredData: Record<string, unknown>;
  // Text stub for crawlers. It is the document body only when the page is
  // not server rendered.
  readonly rootHtml: string;
  // True when the document carries the app's own markup for hydration.
  readonly serverRendered: boolean;
  readonly html: string;
}

/**
 * Renders the React app for one route path. Supplied by the build script from
 * the server entry; omitted in unit tests of the stub output.
 */
export type MarketingAppRenderer = (routePath: string) => string;

interface SegmentPageContent {
  readonly route: SegmentMarketingRoute;
  readonly title: string;
  readonly description: string;
  readonly subtitle: string;
  readonly badge: string;
  readonly audience: string;
  readonly planName: string;
  readonly planDescription: string;
  readonly primaryCta: string;
  readonly secondaryCta: string;
  readonly primaryHref: string;
  readonly pains: readonly string[];
  readonly workflow: readonly string[];
  readonly notFor: readonly string[];
  readonly upgradeTitle: string;
  readonly upgradeBody: string;
  readonly faqs: readonly SegmentFaq[];
  readonly modules: readonly IncludedModule[];
  readonly labels: {
    readonly pains: string;
    readonly workflow: string;
    readonly includes: string;
    readonly faq: string;
  };
}

interface IncludedModule {
  readonly code: ProductCode;
  readonly name: string;
  readonly value: string;
}

// Link previews on WhatsApp, Facebook, LinkedIn and X only render raster
// images, so the social image is a 1200x630 PNG.
export const MARKETING_SOCIAL_IMAGE_PATH = '/graphics/og-frontpage.png';
const MARKETING_SOCIAL_IMAGE_URL = `${CRM_MARKETING_SITE_URL}${MARKETING_SOCIAL_IMAGE_PATH}`;
const NOT_FOUND_ROBOTS_DIRECTIVE = 'noindex,follow';
const MONTHLY_CADENCE: BillingCadence = 'monthly';
const DEFAULT_PRICE_CURRENCY = 'USD';
const MARKETPLACE_PRODUCT_SITEMAPS = [
  `${MARKETPLACE_SITE_URL}/sitemaps/locations-peru.xml`,
  `${MARKETPLACE_SITE_URL}/sitemaps/listings-peru-1.xml`,
  `${MARKETPLACE_SITE_URL}/sitemaps/agencies.xml`,
] as const;

function isObject(
  value: MarketingTranslationValue
): value is MarketingTranslationObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function getNamespace(
  translations: MarketingPrerenderLocale,
  namespace: string
): MarketingTranslationObject {
  switch (namespace) {
    case 'ai':
      return translations.ai;
    case 'common':
      return translations.common;
    case 'comparison':
      return translations.comparison;
    case 'home':
      return translations.home;
    case 'modules':
      return translations.modules;
    case 'pricing':
      return translations.pricing;
    case 'segments':
      return translations.segments;
    default:
      throw new Error(`Unsupported prerender namespace: ${namespace}`);
  }
}

function readValue(
  translations: MarketingPrerenderLocale,
  namespacedKey: string
): MarketingTranslationValue {
  const [namespace, path] = namespacedKey.split(':');
  if (!namespace || !path) {
    throw new Error(`Translation key must include namespace: ${namespacedKey}`);
  }

  const root = getNamespace(translations, namespace);
  return path.split('.').reduce<MarketingTranslationValue>((current, part) => {
    if (!isObject(current) || !(part in current)) {
      throw new Error(`Missing translation key: ${namespacedKey}`);
    }

    return current[part];
  }, root);
}

export function readPrerenderText(
  translations: MarketingPrerenderLocale,
  namespacedKey: string
): string {
  const value = readValue(translations, namespacedKey);
  if (typeof value !== 'string') {
    throw new Error(`Translation key is not text: ${namespacedKey}`);
  }

  return value;
}

function readTextList(
  translations: MarketingPrerenderLocale,
  namespacedKey: string
): readonly string[] {
  const value = readValue(translations, namespacedKey);
  if (
    !Array.isArray(value) ||
    !value.every((item): item is string => typeof item === 'string')
  ) {
    throw new Error(`Translation key is not a string list: ${namespacedKey}`);
  }

  return value;
}

function readFaqList(
  translations: MarketingPrerenderLocale,
  namespacedKey: string
): readonly SegmentFaq[] {
  const value = readValue(translations, namespacedKey);
  if (!Array.isArray(value)) {
    throw new Error(`Translation key is not a FAQ list: ${namespacedKey}`);
  }

  return value.map((item, index) => {
    if (!isObject(item)) {
      throw new Error(`Invalid FAQ item at ${namespacedKey}.${index}`);
    }

    const question = item.question;
    const answer = item.answer;
    if (typeof question !== 'string' || typeof answer !== 'string') {
      throw new Error(`Invalid FAQ item at ${namespacedKey}.${index}`);
    }

    return { question, answer };
  });
}

function renderList(items: readonly string[], className: string): string {
  return `<ul class="${className}">${items
    .map((item) => `<li>${escapeHtml(item)}</li>`)
    .join('')}</ul>`;
}

function renderLink(href: string, label: string): string {
  return `<a href="${escapeHtml(href)}">${escapeHtml(label)}</a>`;
}

function renderJsonLd(id: string, data: Record<string, unknown>): string {
  const serializedData = JSON.stringify(data).replaceAll('</', '<\\/');
  return `<script id="${escapeHtml(id)}" type="application/ld+json">${serializedData}</script>`;
}

const STYLESHEET_LINK_PATTERN =
  /<link\s+rel="stylesheet"[^>]*?\shref="([^"]+)"[^>]*>/g;

/**
 * Replaces the document's stylesheet links with the stylesheets themselves.
 * A linked stylesheet costs a second round trip before the browser may paint;
 * inlined, the page renders as soon as the HTML arrives. The CSP already
 * allows inline styles.
 */
export function inlineLocalStylesheets(
  html: string,
  readStylesheet: (href: string) => string
): string {
  let inlinedStylesheetCount = 0;
  const output = html.replace(
    STYLESHEET_LINK_PATTERN,
    (stylesheetLinkTag: string, href: string) => {
      // The whole tag is replaced; only its href is needed to find the file.
      void stylesheetLinkTag;
      if (!href.startsWith('/') || href.startsWith('//')) {
        throw new Error(`Only local stylesheets can be inlined, got ${href}`);
      }

      const css = readStylesheet(href);
      if (!css.trim()) {
        throw new Error(`Stylesheet ${href} is empty`);
      }
      if (/<\/style/i.test(css)) {
        throw new Error(
          `Stylesheet ${href} would close its own <style> element`
        );
      }

      inlinedStylesheetCount += 1;
      return `<style data-inlined-from="${escapeHtml(href)}">${css}</style>`;
    }
  );

  if (inlinedStylesheetCount === 0) {
    throw new Error('The built document has no stylesheet link to inline');
  }

  return output;
}

function replaceOrInsertHeadTag(
  html: string,
  matcher: RegExp,
  replacement: string
): string {
  if (matcher.test(html)) {
    return html.replace(matcher, replacement);
  }

  return html.replace('</head>', `${replacement}\n  </head>`);
}

function injectMetadata(input: {
  html: string;
  title: string;
  description: string;
  canonicalUrl: string;
  structuredDataId: string;
  structuredData: Record<string, unknown>;
}): string {
  const ogImage = MARKETING_SOCIAL_IMAGE_URL;
  let output = input.html;

  output = replaceOrInsertHeadTag(
    output,
    /<title>[\s\S]*?<\/title>/,
    `<title>${escapeHtml(input.title)}</title>`
  );
  output = replaceOrInsertHeadTag(
    output,
    /<meta\s+name="description"\s+content="[^"]*"\s*\/?>/,
    `<meta name="description" content="${escapeHtml(input.description)}" />`
  );
  output = replaceOrInsertHeadTag(
    output,
    /<meta\s+property="og:title"\s+content="[^"]*"\s*\/?>/,
    `<meta property="og:title" content="${escapeHtml(input.title)}" />`
  );
  output = replaceOrInsertHeadTag(
    output,
    /<meta\s+property="og:description"\s+content="[^"]*"\s*\/?>/,
    `<meta property="og:description" content="${escapeHtml(
      input.description
    )}" />`
  );
  output = replaceOrInsertHeadTag(
    output,
    /<meta\s+property="og:url"\s+content="[^"]*"\s*\/?>/,
    `<meta property="og:url" content="${escapeHtml(input.canonicalUrl)}" />`
  );
  output = replaceOrInsertHeadTag(
    output,
    /<meta\s+property="og:image"\s+content="[^"]*"\s*\/?>/,
    `<meta property="og:image" content="${escapeHtml(ogImage)}" />`
  );
  output = replaceOrInsertHeadTag(
    output,
    /<meta\s+name="twitter:title"\s+content="[^"]*"\s*\/?>/,
    `<meta name="twitter:title" content="${escapeHtml(input.title)}" />`
  );
  output = replaceOrInsertHeadTag(
    output,
    /<meta\s+name="twitter:description"\s+content="[^"]*"\s*\/?>/,
    `<meta name="twitter:description" content="${escapeHtml(
      input.description
    )}" />`
  );
  output = replaceOrInsertHeadTag(
    output,
    /<meta\s+name="twitter:image"\s+content="[^"]*"\s*\/?>/,
    `<meta name="twitter:image" content="${escapeHtml(ogImage)}" />`
  );
  output = replaceOrInsertHeadTag(
    output,
    /<link\s+rel="canonical"\s+href="[^"]*"\s*\/?>/,
    `<link rel="canonical" href="${escapeHtml(input.canonicalUrl)}" />`
  );

  return output.replace(
    '</head>',
    `${renderJsonLd(input.structuredDataId, input.structuredData)}\n  </head>`
  );
}

function buildPageHtml(input: {
  baseHtml: string;
  title: string;
  description: string;
  canonicalUrl: string;
  structuredDataId: string;
  structuredData: Record<string, unknown>;
  rootHtml: string;
  serverRendered?: boolean;
}): string {
  const withMetadata = injectMetadata({
    html: input.baseHtml,
    title: input.title,
    description: input.description,
    canonicalUrl: input.canonicalUrl,
    structuredDataId: input.structuredDataId,
    structuredData: input.structuredData,
  });
  const rootOpeningTag = input.serverRendered
    ? `<div id="root" ${SERVER_RENDERED_ROOT_ATTRIBUTE}="true">`
    : '<div id="root">';
  // A replacer function keeps `$` sequences in the markup (prices, React's
  // Suspense comments) from being read as replacement patterns.
  return withMetadata.replace(
    /<div\s+id="root"><\/div>/,
    () => `${rootOpeningTag}${input.rootHtml}</div>`
  );
}

function resolveIncludedModules(
  route: SegmentMarketingRoute,
  translations: MarketingPrerenderLocale
): readonly IncludedModule[] {
  return REAL_ESTATE_PLAN_MARKETING_PRODUCT_CODES[route.planCode].map(
    (moduleCode) => {
      const product = commercialProducts.find(
        (candidate) => candidate.code === moduleCode
      );

      if (!product) {
        throw new Error(
          `Plan ${route.planCode} references unknown marketing module ${moduleCode}`
        );
      }

      return {
        code: product.code,
        name: readPrerenderText(translations, product.nameKey),
        value: readPrerenderText(translations, product.valueKey),
      };
    }
  );
}

export function buildSegmentStructuredData(input: {
  route: SegmentMarketingRoute;
  title: string;
  description: string;
  planName: string;
  faqs: readonly SegmentFaq[];
}): Record<string, unknown> {
  const pageUrl = buildCrmMarketingUrl(input.route.path);
  const offer =
    input.route.planCode === 'free'
      ? {
          '@type': 'Offer',
          name: input.planName,
          price: '0',
          priceCurrency: DEFAULT_PRICE_CURRENCY,
          availability: 'https://schema.org/InStock',
          url: pageUrl,
        }
      : {
          '@type': 'Offer',
          name: input.planName,
          price: String(
            getPlanStartingPriceUsd(input.route.planCode, MONTHLY_CADENCE)
          ),
          priceCurrency: DEFAULT_PRICE_CURRENCY,
          availability: 'https://schema.org/InStock',
          url: pageUrl,
        };

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'SoftwareApplication',
        '@id': `${pageUrl}#software`,
        name: input.title,
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Cloud',
        url: pageUrl,
        description: input.description,
        offers: offer,
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${pageUrl}#breadcrumb`,
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Inmueble.red CRM',
            item: buildCrmMarketingUrl('/'),
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: input.title,
            item: pageUrl,
          },
        ],
      },
      {
        '@type': 'FAQPage',
        '@id': `${pageUrl}#faq`,
        mainEntity: input.faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer,
          },
        })),
      },
    ],
  };
}

function buildHomeStructuredData(input: {
  description: string;
  planName: string;
  registerUrl: string;
}): Record<string, unknown> {
  const pageUrl = buildCrmMarketingUrl('/');
  const organizationId = `${pageUrl}#organization`;

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': organizationId,
        name: 'Inmueble.red',
        url: pageUrl,
      },
      {
        '@type': 'WebSite',
        '@id': `${pageUrl}#website`,
        name: 'Inmueble.red CRM',
        url: pageUrl,
        description: input.description,
        publisher: {
          '@id': organizationId,
        },
        potentialAction: {
          '@type': 'SearchAction',
          target: `${MARKETPLACE_SITE_URL}/peru?search={search_term_string}`,
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'SoftwareApplication',
        '@id': `${pageUrl}#crm-application`,
        name: 'Inmueble.red CRM',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Cloud',
        url: pageUrl,
        description: input.description,
        offers: {
          '@type': 'Offer',
          name: input.planName,
          price: '0',
          priceCurrency: DEFAULT_PRICE_CURRENCY,
          availability: 'https://schema.org/InStock',
          url: input.registerUrl,
        },
      },
    ],
  };
}

function buildWebPageStructuredData(input: {
  title: string;
  description: string;
  canonicalUrl: string;
}): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${input.canonicalUrl}#webpage`,
    name: input.title,
    url: input.canonicalUrl,
    description: input.description,
  };
}

function readSegmentPageContent(input: {
  route: SegmentMarketingRoute;
  translations: MarketingPrerenderLocale;
  registerUrl: string;
}): SegmentPageContent {
  const translationBase = `segments:segments.${input.route.segmentCode}`;
  const title = readPrerenderText(input.translations, input.route.titleKey);
  const description = readPrerenderText(
    input.translations,
    input.route.metaDescriptionKey
  );
  const planName = readPrerenderText(
    input.translations,
    `pricing:plans.${input.route.planCode}.name`
  );

  return {
    route: input.route,
    title,
    description,
    subtitle: readPrerenderText(
      input.translations,
      `${translationBase}.subtitle`
    ),
    badge: readPrerenderText(input.translations, input.route.badgeKey),
    audience: readPrerenderText(
      input.translations,
      `${translationBase}.audience`
    ),
    planName,
    planDescription: readPrerenderText(
      input.translations,
      `pricing:plans.${input.route.planCode}.description`
    ),
    primaryCta: readPrerenderText(
      input.translations,
      input.route.primaryCtaKey
    ),
    secondaryCta: readPrerenderText(
      input.translations,
      input.route.secondaryCtaKey
    ),
    primaryHref:
      input.route.primaryCta === 'freeRegister'
        ? input.registerUrl
        : '#segment-demo-form',
    pains: readTextList(input.translations, `${translationBase}.pains`),
    workflow: readTextList(input.translations, `${translationBase}.workflow`),
    notFor: readTextList(input.translations, `${translationBase}.notFor`),
    upgradeTitle: readPrerenderText(
      input.translations,
      `${translationBase}.upgradeTitle`
    ),
    upgradeBody: readPrerenderText(
      input.translations,
      `${translationBase}.upgradeBody`
    ),
    faqs: readFaqList(input.translations, `${translationBase}.faqs`),
    modules: resolveIncludedModules(input.route, input.translations),
    labels: {
      pains: readPrerenderText(input.translations, 'segments:common.pains'),
      workflow: readPrerenderText(
        input.translations,
        'segments:common.workflow'
      ),
      includes: readPrerenderText(
        input.translations,
        'segments:common.includes'
      ),
      faq: readPrerenderText(input.translations, 'segments:common.faq'),
    },
  };
}

function renderSegmentRoot(content: SegmentPageContent): string {
  const moduleHtml = content.modules
    .map(
      (module) =>
        `<article><h3>${escapeHtml(module.name)}</h3><p>${escapeHtml(
          module.value
        )}</p></article>`
    )
    .join('');
  const faqHtml = content.faqs
    .map(
      (faq) =>
        `<article><h3>${escapeHtml(faq.question)}</h3><p>${escapeHtml(
          faq.answer
        )}</p></article>`
    )
    .join('');

  return [
    '<main data-prerender="marketing-segment">',
    `<p>${escapeHtml(content.badge)}</p>`,
    `<h1>${escapeHtml(content.title)}</h1>`,
    `<p>${escapeHtml(content.subtitle)}</p>`,
    `<p>${escapeHtml(content.audience)}</p>`,
    `<p><strong>${escapeHtml(content.planName)}</strong> ${escapeHtml(
      content.planDescription
    )}</p>`,
    renderLink(content.primaryHref, content.primaryCta),
    renderLink(content.route.secondaryHref, content.secondaryCta),
    '<section>',
    `<h2>${escapeHtml(content.labels.pains)}</h2>`,
    renderList(content.pains, 'segment-pains'),
    '</section>',
    '<section>',
    `<h2>${escapeHtml(content.labels.workflow)}</h2>`,
    renderList(content.workflow, 'segment-workflow'),
    '</section>',
    '<section>',
    `<h2>${escapeHtml(content.labels.includes)}</h2>`,
    moduleHtml,
    '</section>',
    '<section>',
    `<h2>${escapeHtml(content.upgradeTitle)}</h2>`,
    `<p>${escapeHtml(content.upgradeBody)}</p>`,
    renderList(content.notFor, 'segment-not-for'),
    '</section>',
    `<section><h2>${escapeHtml(content.labels.faq)}</h2>${faqHtml}</section>`,
    '</main>',
  ].join('');
}

function buildSegmentPage(input: {
  baseHtml: string;
  route: SegmentMarketingRoute;
  translations: MarketingPrerenderLocale;
  registerUrl: string;
}): MarketingPrerenderPage {
  const content = readSegmentPageContent(input);
  const title = `${content.title} | Inmueble.red`;
  const canonicalUrl = buildCrmMarketingUrl(content.route.path);
  const structuredDataId = `structured-data-segment-${content.route.segmentCode}`;
  const structuredData = buildSegmentStructuredData({
    route: content.route,
    title: content.title,
    description: content.description,
    planName: content.planName,
    faqs: content.faqs,
  });
  const rootHtml = renderSegmentRoot(content);

  return {
    route: {
      kind: content.route.kind,
      path: content.route.path,
      sitemapGroup: 'crm',
    },
    title,
    description: content.description,
    canonicalUrl,
    structuredDataId,
    structuredData,
    rootHtml,
    serverRendered: false,
    html: buildPageHtml({
      baseHtml: input.baseHtml,
      title,
      description: content.description,
      canonicalUrl,
      structuredDataId,
      structuredData,
      rootHtml,
    }),
  };
}

function buildHomePage(input: {
  baseHtml: string;
  route: IndexedMarketingRoute;
  translations: MarketingPrerenderLocale;
  registerUrl: string;
}): MarketingPrerenderPage {
  const title = readPrerenderText(input.translations, 'home:meta.title');
  const description = readPrerenderText(
    input.translations,
    'home:meta.description'
  );
  const heroTitle = `${readPrerenderText(
    input.translations,
    'home:hero.title'
  )} ${readPrerenderText(input.translations, 'home:hero.titleHighlight')}`;
  const segmentLinks = segmentMarketingRoutes
    .map((route) => {
      const planName = readPrerenderText(
        input.translations,
        `pricing:plans.${route.planCode}.name`
      );
      const descriptionText = readPrerenderText(
        input.translations,
        `pricing:plans.${route.planCode}.description`
      );
      return `<li>${renderLink(route.path, planName)}<p>${escapeHtml(
        descriptionText
      )}</p></li>`;
    })
    .join('');
  const rootHtml = [
    '<main data-prerender="marketing-home">',
    `<h1>${escapeHtml(heroTitle)}</h1>`,
    `<p>${escapeHtml(readPrerenderText(input.translations, 'home:hero.subtitle'))}</p>`,
    renderLink(
      input.registerUrl,
      readPrerenderText(input.translations, 'common:cta.startFree')
    ),
    renderLink(
      `${MARKETPLACE_SITE_URL}/peru`,
      readPrerenderText(input.translations, 'common:nav.marketplaceSearch')
    ),
    `<section><h2>${escapeHtml(
      readPrerenderText(input.translations, 'segments:hub.title')
    )}</h2><p>${escapeHtml(
      readPrerenderText(input.translations, 'segments:hub.subtitle')
    )}</p><ul>${segmentLinks}</ul></section>`,
    '</main>',
  ].join('');
  const canonicalUrl = buildCrmMarketingUrl(input.route.path);
  const structuredDataId = 'structured-data-home-software';
  const structuredData = buildHomeStructuredData({
    description,
    planName: readPrerenderText(input.translations, 'pricing:plans.free.name'),
    registerUrl: input.registerUrl,
  });

  return {
    route: input.route,
    title,
    description,
    canonicalUrl,
    structuredDataId,
    structuredData,
    rootHtml,
    serverRendered: false,
    html: buildPageHtml({
      baseHtml: input.baseHtml,
      title,
      description,
      canonicalUrl,
      structuredDataId,
      structuredData,
      rootHtml,
    }),
  };
}

function buildLegacyProductPage(input: {
  baseHtml: string;
  route: IndexedMarketingRoute;
  translations: MarketingPrerenderLocale;
}): MarketingPrerenderPage {
  const legacyRoute = legacyProductMarketingRoutes.find(
    (candidate) => candidate.path === input.route.path
  );
  if (!legacyRoute) {
    throw new Error(`Missing legacy product route for ${input.route.path}`);
  }

  const translationBase = `common:productRoutes.${legacyRoute.translationKey}`;
  const pageTitle = readPrerenderText(
    input.translations,
    `${translationBase}.title`
  );
  const description = readPrerenderText(
    input.translations,
    `${translationBase}.subtitle`
  );
  const items = readTextList(input.translations, `${translationBase}.items`);
  const title = `${pageTitle} | Inmueble.red`;
  const canonicalUrl = buildCrmMarketingUrl(input.route.path);
  const structuredDataId = `structured-data-legacy-${legacyRoute.routeKey}`;
  const structuredData = buildWebPageStructuredData({
    title: pageTitle,
    description,
    canonicalUrl,
  });
  const rootHtml = [
    '<main data-prerender="marketing-legacy-product">',
    `<h1>${escapeHtml(pageTitle)}</h1>`,
    `<p>${escapeHtml(description)}</p>`,
    renderList(items, 'legacy-product-items'),
    '</main>',
  ].join('');

  return {
    route: input.route,
    title,
    description,
    canonicalUrl,
    structuredDataId,
    structuredData,
    rootHtml,
    serverRendered: false,
    html: buildPageHtml({
      baseHtml: input.baseHtml,
      title,
      description,
      canonicalUrl,
      structuredDataId,
      structuredData,
      rootHtml,
    }),
  };
}

function resolveStandardPageMetadata(
  route: IndexedMarketingRoute,
  translations: MarketingPrerenderLocale
): {
  readonly title: string;
  readonly description: string;
  readonly h1: string;
} {
  switch (route.path) {
    case '/precios/':
      return {
        title: readPrerenderText(translations, 'pricing:meta.title'),
        description: readPrerenderText(
          translations,
          'pricing:meta.description'
        ),
        h1: readPrerenderText(translations, 'pricing:title'),
      };
    case '/modulos/':
      return {
        title: readPrerenderText(translations, 'modules:meta.title'),
        description: readPrerenderText(
          translations,
          'modules:meta.description'
        ),
        h1: readPrerenderText(translations, 'modules:title'),
      };
    case '/ia/':
      return {
        title: readPrerenderText(translations, 'ai:meta.title'),
        description: readPrerenderText(translations, 'ai:meta.description'),
        h1: readPrerenderText(translations, 'ai:hero.title'),
      };
    case '/comparar/':
      return {
        title: readPrerenderText(translations, 'comparison:meta.title'),
        description: readPrerenderText(
          translations,
          'comparison:meta.description'
        ),
        h1: readPrerenderText(translations, 'comparison:hero.title'),
      };
    case '/privacy':
      return {
        title: 'Política de privacidad | Inmueble.red',
        description:
          'Política de privacidad de Inmueble.red para usuarios del CRM y canales públicos.',
        h1: 'Política de privacidad',
      };
    case '/terms':
      return {
        title: 'Términos de servicio | Inmueble.red',
        description:
          'Términos de servicio de Inmueble.red para uso del CRM y servicios asociados.',
        h1: 'Términos de servicio',
      };
    default:
      throw new Error(`Unsupported standard prerender route: ${route.path}`);
  }
}

function buildStandardPage(input: {
  baseHtml: string;
  route: IndexedMarketingRoute;
  translations: MarketingPrerenderLocale;
}): MarketingPrerenderPage {
  const metadata = resolveStandardPageMetadata(input.route, input.translations);
  const canonicalUrl = buildCrmMarketingUrl(input.route.path);
  const structuredDataId = `structured-data-standard-${input.route.path.replaceAll(
    '/',
    '-'
  )}`;
  const structuredData = buildWebPageStructuredData({
    title: metadata.title,
    description: metadata.description,
    canonicalUrl,
  });
  const rootHtml = [
    '<main data-prerender="marketing-standard">',
    `<h1>${escapeHtml(metadata.h1)}</h1>`,
    `<p>${escapeHtml(metadata.description)}</p>`,
    '</main>',
  ].join('');

  return {
    route: input.route,
    title: metadata.title,
    description: metadata.description,
    canonicalUrl,
    structuredDataId,
    structuredData,
    rootHtml,
    serverRendered: false,
    html: buildPageHtml({
      baseHtml: input.baseHtml,
      title: metadata.title,
      description: metadata.description,
      canonicalUrl,
      structuredDataId,
      structuredData,
      rootHtml,
    }),
  };
}

// Pages outside the indexed route list (404 and the sign-in bridge) must not
// carry the home page's canonical URL or be indexed as a copy of it.
function buildUnindexedShellHtml(input: {
  baseHtml: string;
  title: string;
  description: string;
  rootHtml: string;
}): string {
  let output = input.baseHtml;

  output = replaceOrInsertHeadTag(
    output,
    /<title>[\s\S]*?<\/title>/,
    `<title>${escapeHtml(input.title)}</title>`
  );
  output = replaceOrInsertHeadTag(
    output,
    /<meta\s+name="description"\s+content="[^"]*"\s*\/?>/,
    `<meta name="description" content="${escapeHtml(input.description)}" />`
  );
  output = replaceOrInsertHeadTag(
    output,
    /<meta\s+name="robots"\s+content="[^"]*"\s*\/?>/,
    `<meta name="robots" content="${NOT_FOUND_ROBOTS_DIRECTIVE}" />`
  );
  output = replaceOrInsertHeadTag(
    output,
    /<meta\s+property="og:title"\s+content="[^"]*"\s*\/?>/,
    `<meta property="og:title" content="${escapeHtml(input.title)}" />`
  );
  output = replaceOrInsertHeadTag(
    output,
    /<meta\s+property="og:description"\s+content="[^"]*"\s*\/?>/,
    `<meta property="og:description" content="${escapeHtml(
      input.description
    )}" />`
  );
  output = output
    .replace(/\s*<link\s+rel="canonical"\s+href="[^"]*"\s*\/?>/, '')
    .replace(/\s*<meta\s+property="og:url"\s+content="[^"]*"\s*\/?>/, '');

  return output.replace(
    /<div\s+id="root"><\/div>/,
    `<div id="root">${input.rootHtml}</div>`
  );
}

/**
 * Static 404 document. Cloudflare Pages serves a top-level 404.html with a
 * real 404 status for unknown paths; without it every unknown URL answers 200
 * with the home page markup.
 */
export function buildMarketingNotFoundHtml(input: {
  baseHtml: string;
  translations: MarketingPrerenderLocale;
}): string {
  const heading = readPrerenderText(
    input.translations,
    'common:notFound.title'
  );
  const body = readPrerenderText(input.translations, 'common:notFound.body');
  const rootHtml = [
    '<main data-prerender="marketing-not-found">',
    `<h1>${escapeHtml(heading)}</h1>`,
    `<p>${escapeHtml(body)}</p>`,
    renderLink(
      '/',
      readPrerenderText(input.translations, 'common:notFound.primaryCta')
    ),
    renderLink(
      '/precios/',
      readPrerenderText(input.translations, 'common:notFound.secondaryCta')
    ),
    '</main>',
  ].join('');

  return buildUnindexedShellHtml({
    baseHtml: input.baseHtml,
    title: readPrerenderText(input.translations, 'common:notFound.meta.title'),
    description: readPrerenderText(
      input.translations,
      'common:notFound.meta.description'
    ),
    rootHtml,
  });
}

/**
 * Static shell for /login. The route only exists in the client router (it
 * forwards to the CRM sign-in), so it needs its own document once unknown
 * paths answer 404.
 */
export function buildMarketingLoginBridgeHtml(input: {
  baseHtml: string;
  translations: MarketingPrerenderLocale;
}): string {
  const loginLabel = readPrerenderText(input.translations, 'common:nav.login');
  const brandName = readPrerenderText(input.translations, 'common:brand.name');

  return buildUnindexedShellHtml({
    baseHtml: input.baseHtml,
    title: `${loginLabel} | ${brandName}`,
    description: loginLabel,
    rootHtml: '',
  });
}

export function buildMarketingPrerenderPage(input: {
  baseHtml: string;
  route: IndexedMarketingRoute;
  translations: MarketingPrerenderLocale;
  registerUrl: string;
  renderApp?: MarketingAppRenderer;
}): MarketingPrerenderPage {
  const stubPage = buildMarketingStubPage(input);
  if (!input.renderApp || !isServerRenderedMarketingRoute(input.route)) {
    return stubPage;
  }

  const appHtml = input.renderApp(input.route.path);
  if (!appHtml.trim()) {
    throw new Error(`Server render of ${input.route.path} produced no markup`);
  }

  return {
    ...stubPage,
    serverRendered: true,
    html: buildPageHtml({
      baseHtml: input.baseHtml,
      title: stubPage.title,
      description: stubPage.description,
      canonicalUrl: stubPage.canonicalUrl,
      structuredDataId: stubPage.structuredDataId,
      structuredData: stubPage.structuredData,
      rootHtml: appHtml,
      serverRendered: true,
    }),
  };
}

function buildMarketingStubPage(input: {
  baseHtml: string;
  route: IndexedMarketingRoute;
  translations: MarketingPrerenderLocale;
  registerUrl: string;
}): MarketingPrerenderPage {
  if (input.route.kind === 'home') {
    return buildHomePage(input);
  }

  if (input.route.kind === 'segment') {
    const segmentRoute = segmentMarketingRoutes.find(
      (candidate) => candidate.path === input.route.path
    );

    if (!segmentRoute) {
      throw new Error(`Missing segment route for ${input.route.path}`);
    }

    return buildSegmentPage({
      baseHtml: input.baseHtml,
      route: segmentRoute,
      translations: input.translations,
      registerUrl: input.registerUrl,
    });
  }

  if (input.route.kind === 'legacyProduct') {
    return buildLegacyProductPage(input);
  }

  return buildStandardPage(input);
}

export function buildAllMarketingPrerenderPages(input: {
  baseHtml: string;
  translations: MarketingPrerenderLocale;
  registerUrl: string;
  routes?: readonly IndexedMarketingRoute[];
  renderApp?: MarketingAppRenderer;
}): readonly MarketingPrerenderPage[] {
  const routes = input.routes ?? indexedMarketingRoutes;
  return routes.map((route) =>
    buildMarketingPrerenderPage({
      baseHtml: input.baseHtml,
      route,
      translations: input.translations,
      registerUrl: input.registerUrl,
      renderApp: input.renderApp,
    })
  );
}

export function buildMarketingProductSitemapXml(
  routes: readonly IndexedMarketingRoute[] = indexedMarketingRoutes
): string {
  const urls = routes
    .filter((route) => route.sitemapGroup === 'crm')
    .map(
      (route) =>
        `  <url>\n    <loc>${escapeHtml(
          buildCrmMarketingUrl(route.path)
        )}</loc>\n  </url>`
    )
    .join('\n');

  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">',
    urls,
    '</urlset>',
    '',
  ].join('\n');
}

export function buildMarketingSitemapIndexXml(): string {
  const sitemaps = [
    `${CRM_MARKETING_SITE_URL}/sitemaps/product-pages.xml`,
    ...MARKETPLACE_PRODUCT_SITEMAPS,
  ]
    .map(
      (url) => `  <sitemap>\n    <loc>${escapeHtml(url)}</loc>\n  </sitemap>`
    )
    .join('\n');

  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<sitemapindex xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">',
    sitemaps,
    '</sitemapindex>',
    '',
  ].join('\n');
}

export function buildMarketingRobotsTxt(): string {
  return [
    'User-agent: *',
    'Allow: /',
    '',
    `Sitemap: ${CRM_MARKETING_SITE_URL}/sitemap.xml`,
    '',
  ].join('\n');
}

export function assertPaidSegmentsUseDemoCta(): void {
  for (const route of segmentMarketingRoutes) {
    if (route.planCode === 'free') {
      if (route.primaryCta !== 'freeRegister') {
        throw new Error('Free segment must route to free registration');
      }
      continue;
    }

    if (route.primaryCta !== 'demoForm') {
      throw new Error(
        `Paid segment ${route.segmentCode} must route to demo form`
      );
    }
  }
}

export function assertPlanCodesMatchSegmentNames(
  translations: MarketingPrerenderLocale
): void {
  const expectedPlanNames: Record<PlanCode, string> = {
    free: 'Agente inmobiliario / Dueño directo',
    basic: 'Inmobiliaria / Promotora',
    plus: 'Desarrolladora',
    pro: 'Desarrolladora Financiera',
  };

  for (const [planCode, expectedName] of Object.entries(expectedPlanNames)) {
    const actualName = readPrerenderText(
      translations,
      `pricing:plans.${planCode}.name`
    );
    if (actualName !== expectedName) {
      throw new Error(
        `Plan ${planCode} is named ${actualName}; expected ${expectedName}`
      );
    }
  }
}
